import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const h = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmt = (n) => Number(n || 0).toLocaleString('uz-UZ');
const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + ' mln';
  if (v >= 1_000) return (v / 1_000).toFixed(0) + ' ming';
  return v.toLocaleString();
};

const KPI = ({ icon, label, value, sub, color = '#27E0FF' }) => (
  <div style={{
    background: 'var(--bg2)', border: `1px solid ${color}22`,
    borderRadius: 14, padding: '20px 24px', flex: 1, minWidth: 160,
  }}>
    <div style={{ fontSize: 28 }}>{icon}</div>
    <div style={{ fontSize: 28, fontWeight: 900, color, marginTop: 8 }}>{value}</div>
    <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{label}</div>
    {sub && <div style={{ fontSize: 11, color: color + '99', marginTop: 4 }}>{sub}</div>}
  </div>
);

export default function SuperDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/super/dashboard`, h())
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: 'var(--text3)', padding: 40 }}>Yuklanmoqda...</div>;
  if (!data) return <div style={{ color: 'var(--red)', padding: 40 }}>Xatolik</div>;

  const { stats, subscriptions, expiring, chart } = data;

  return (
    <div style={{ maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
        <div style={{ fontSize: 28 }}>👑</div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#f59e0b' }}>Super Admin — Platforma</div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>Barcha klublar bo'yicha umumiy ko'rinish</div>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <KPI icon="🏢" label="Jami klublar" value={fmt(stats.total_labs)} color="#27E0FF" />
        <KPI icon="✅" label="Faol klublar" value={fmt(stats.active_labs)} sub={`${stats.trial_labs} ta trial`} color="#10b981" />
        <KPI icon="🖥️" label="Aktiv sessiyalar" value={fmt(stats.active_sessions)} color="#a78bfa" />
        <KPI icon="👥" label="Foydalanuvchilar" value={fmt(stats.total_users)} color="#60a5fa" />
        <KPI icon="💰" label="Bugungi daromad" value={fmtM(stats.revenue_today)} sub="so'm" color="#f59e0b" />
        <KPI icon="📈" label="Oylik daromad" value={fmtM(stats.revenue_month)} sub="so'm" color="#f59e0b" />
      </div>

      {/* Chart + Subs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, marginBottom: 24 }}>
        {/* 7-kun grafik */}
        <div style={{ background: 'var(--bg2)', borderRadius: 14, padding: 24, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 20 }}>📊 7 kunlik daromad</div>
          {chart && chart.length > 0 ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120 }}>
                {chart.map((d, i) => {
                  const max = Math.max(...chart.map(x => Number(x.total || 0)));
                  const pct = max > 0 ? (Number(d.total || 0) / max) * 100 : 0;
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <div style={{ fontSize: 9, color: 'var(--text3)' }}>{fmtM(d.total)}</div>
                      <div style={{
                        width: '100%', height: `${Math.max(pct, 4)}%`,
                        background: 'linear-gradient(180deg, #f59e0b, #f59e0b44)',
                        borderRadius: '4px 4px 0 0', minHeight: 4,
                      }} />
                      <div style={{ fontSize: 9, color: 'var(--text3)' }}>
                        {new Date(d.day).toLocaleDateString('uz-UZ', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={{ color: 'var(--text3)', textAlign: 'center', padding: 40 }}>Ma'lumot yo'q</div>
          )}
        </div>

        {/* Obuna statusi */}
        <div style={{ background: 'var(--bg2)', borderRadius: 14, padding: 24, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 20 }}>📋 Obuna holati</div>
          {[
            { key: 'active', label: 'Faol', color: '#10b981' },
            { key: 'trial', label: 'Trial', color: '#f59e0b' },
            { key: 'expired', label: 'Muddati o\'tgan', color: '#ef4444' },
            { key: 'suspended', label: 'To\'xtatilgan', color: '#6b7280' },
          ].map(({ key, label, color }) => (
            <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
                <span style={{ fontSize: 13, color: 'var(--text2)' }}>{label}</span>
              </div>
              <span style={{ fontWeight: 700, color }}>{subscriptions?.[key] || 0}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Muddati yaqin klublar */}
      {expiring && expiring.length > 0 && (
        <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 14, padding: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#ef4444', marginBottom: 16 }}>
            ⚠️ 7 kun ichida obunasi tugaydi ({expiring.length} ta klub)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {expiring.map(lab => (
              <div key={lab.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: 'rgba(239,68,68,0.05)', borderRadius: 10, padding: '10px 16px',
              }}>
                <div>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{lab.name}</span>
                  <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 8 }}>{lab.owner_email}</span>
                </div>
                <div style={{ fontSize: 12, color: '#ef4444', fontWeight: 700 }}>
                  {new Date(lab.sub_end).toLocaleDateString('uz-UZ')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
